/**
 * Support-requests view inside the Scout widget: list your tickets, read
 * platform-team replies, add information, or raise a new request manually.
 * (Scout can also raise one conversationally via its ticket tool.)
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  supportTicketsApi,
  type SupportTicketSummary,
} from "@/api/support-tickets";

const STATUS_STYLE: Record<string, string> = {
  open: "bg-accent/10 text-accent",
  in_progress: "bg-warning/15 text-warning",
  resolved: "bg-success/15 text-success",
  closed: "bg-surface-hover text-text-muted",
};

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 rounded-full px-2 py-[2px] text-[10.5px] font-semibold",
        STATUS_STYLE[status] ?? STATUS_STYLE.closed,
      )}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SupportPanel({
  tenantId,
  initialTicketId,
}: {
  tenantId: string;
  initialTicketId?: string | null;
}) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(initialTicketId ?? null);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [notifyChannel, setNotifyChannel] = useState("in_app");
  const [comment, setComment] = useState("");

  const { data: channels } = useQuery({
    queryKey: ["support-channels", tenantId],
    queryFn: () => supportTicketsApi.channels(tenantId),
    enabled: creating,
    staleTime: 5 * 60_000,
  });

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ["support-tickets", tenantId],
    queryFn: () => supportTicketsApi.list(tenantId),
  });

  const { data: detail } = useQuery({
    queryKey: ["support-ticket", tenantId, selectedId],
    queryFn: () => supportTicketsApi.get(tenantId, selectedId!),
    enabled: !!selectedId,
  });

  const create = useMutation({
    mutationFn: () =>
      supportTicketsApi.create(tenantId, {
        subject: subject.trim(),
        description: description.trim(),
        notifyChannel,
        context: { path: window.location.pathname },
      }),
    onSuccess: (ticket) => {
      setCreating(false);
      setSubject("");
      setDescription("");
      setNotifyChannel("in_app");
      setSelectedId(ticket.id);
      qc.invalidateQueries({ queryKey: ["support-tickets", tenantId] });
    },
  });

  const addComment = useMutation({
    mutationFn: () => supportTicketsApi.addComment(tenantId, selectedId!, comment.trim()),
    onSuccess: () => {
      setComment("");
      qc.invalidateQueries({ queryKey: ["support-ticket", tenantId, selectedId] });
    },
  });

  // ── New request form ───────────────────────────────────────────────
  if (creating) {
    return (
      <div data-tour="support-form" className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        <button
          onClick={() => setCreating(false)}
          className="flex items-center gap-1.5 self-start text-[12px] text-text-muted hover:text-text-primary"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <p className="m-0 text-[13px] font-semibold text-text-primary">New support request</p>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          maxLength={200}
          className="rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-text-primary outline-none placeholder:text-text-muted focus:ring-1 focus:ring-accent"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the problem or request — include which agent or workflow it involves"
          rows={6}
          className="resize-none rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-text-primary outline-none placeholder:text-text-muted focus:ring-1 focus:ring-accent"
        />
        <div>
          <p className="m-0 mb-1.5 text-[12px] font-medium text-text-secondary">
            Where should updates reach you?
          </p>
          <div className="flex flex-col gap-1">
            {[
              { key: "in_app", label: "In the platform (bell)", available: true },
              {
                key: "teams",
                label: channels?.teams
                  ? "Microsoft Teams (ProClaw app DM)"
                  : "Microsoft Teams — install the ProClaw Teams app to enable",
                available: channels?.teams ?? false,
              },
              {
                key: "email",
                label: channels?.email
                  ? `Email (${channels.emailAddress})`
                  : "Email — not available for your company",
                available: channels?.email ?? false,
              },
            ].map((c) => (
              <label
                key={c.key}
                className={cn(
                  "flex items-center gap-2 text-[12.5px]",
                  c.available ? "text-text-primary" : "text-text-muted",
                )}
              >
                <input
                  type="radio"
                  name="notify-channel"
                  checked={notifyChannel === c.key}
                  disabled={!c.available}
                  onChange={() => setNotifyChannel(c.key)}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>
        <button
          onClick={() => create.mutate()}
          disabled={!subject.trim() || !description.trim() || create.isPending}
          className="flex items-center justify-center gap-2 rounded-lg bg-accent px-3 py-2 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Submit request
        </button>
        {create.isError && (
          <p className="m-0 text-[12px] text-danger">Could not submit — please try again.</p>
        )}
      </div>
    );
  }

  // ── Ticket detail ──────────────────────────────────────────────────
  if (selectedId) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text-primary"
          >
            <ArrowLeft size={13} /> All requests
          </button>
          {detail ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <p className="m-0 text-[13.5px] font-semibold text-text-primary">
                  {detail.ticket.subject}
                </p>
                <StatusChip status={detail.ticket.status} />
              </div>
              <div className="rounded-lg border border-border bg-background px-3 py-2 text-[12.5px] whitespace-pre-wrap text-text-secondary">
                {detail.ticket.description}
              </div>
              {detail.comments.map((c) => (
                <div
                  key={c.id}
                  className={cn(
                    "rounded-lg px-3 py-2 text-[12.5px]",
                    c.authorType === "platform_team"
                      ? "border border-accent/25 bg-accent/5 text-text-primary"
                      : "border border-border bg-background text-text-secondary",
                  )}
                >
                  <p className="m-0 mb-1 text-[11px] font-semibold text-text-muted">
                    {c.authorType === "platform_team"
                      ? "Platform team"
                      : c.authorName || "You"}{" "}
                    · {formatWhen(c.createdAt)}
                  </p>
                  <p className="m-0 whitespace-pre-wrap">{c.content}</p>
                </div>
              ))}
            </>
          ) : (
            <div className="flex items-center gap-2 text-[12.5px] text-text-muted">
              <Loader2 size={13} className="animate-spin" /> Loading…
            </div>
          )}
        </div>
        {detail && detail.ticket.status !== "closed" && (
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-3 py-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add information…"
                rows={1}
                className="max-h-20 min-h-[22px] flex-1 resize-none bg-transparent text-[13px] text-text-primary outline-none placeholder:text-text-muted"
              />
              <button
                onClick={() => addComment.mutate()}
                disabled={!comment.trim() || addComment.isPending}
                aria-label="Send comment"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent text-white hover:bg-accent-hover disabled:opacity-40"
              >
                {addComment.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Ticket list ────────────────────────────────────────────────────
  const tickets: SupportTicketSummary[] = listData?.tickets ?? [];
  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4">
      <div className="flex items-center justify-between">
        <p className="m-0 text-[13px] font-semibold text-text-primary">Support requests</p>
        <button
          onClick={() => setCreating(true)}
          data-tour="support-new"
          className="flex items-center gap-1 rounded-lg bg-accent px-2.5 py-1.5 text-[12px] font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={13} /> New
        </button>
      </div>
      {listLoading ? (
        <div className="flex items-center gap-2 text-[12.5px] text-text-muted">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : tickets.length === 0 ? (
        <p className="m-0 py-6 text-center text-[12.5px] text-text-muted">
          No support requests yet. Raise one here, or ask Scout to log one for you.
        </p>
      ) : (
        tickets.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelectedId(t.id)}
            className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-left hover:bg-surface-hover"
          >
            <div className="min-w-0">
              <p className="m-0 truncate text-[13px] font-medium text-text-primary">{t.subject}</p>
              <p className="m-0 mt-0.5 text-[11.5px] text-text-muted">
                Updated {formatWhen(t.updatedAt)}
              </p>
            </div>
            <StatusChip status={t.status} />
          </button>
        ))
      )}
    </div>
  );
}
